import { SpeedModifier, CharacterSpeedData } from '@/types';

export class CharacterSpeedManager {
  private static speedModeBaseSpeeds: Record<number, number> = {
    0: 4.0, // SpeedModeID 0 - Standard (default)
    1: 5.0, // SpeedModeID 1 - Faster
    2: 6.0, // SpeedModeID 2 - Fastest
    3: 3.0, // SpeedModeID 3 - Slow
    4: 2.0, // SpeedModeID 4 - Slower
    5: 1.0, // SpeedModeID 5 - Slowest
    6: 0.0, // SpeedModeID 6 - Stunt (no movement)
  };

  /**
   * Get the base movement speed for a specific speed mode
   */
  static getBaseSpeed(speedModeId: number): number {
    return this.speedModeBaseSpeeds[speedModeId] ?? this.speedModeBaseSpeeds[0];
  }

  /**
   * Calculate the current effective speed including all active modifiers
   */
  static getCurrentSpeed(speedData: CharacterSpeedData): number {
    const currentTime = Date.now();

    // Filter out expired modifiers
    const activeModifiers = speedData.modifiers.filter(modifier => {
      if (modifier.duration === 0) return true; // Permanent modifiers
      return (currentTime - modifier.startTime) < modifier.duration;
    });

    // Calculate total speed multiplier from all active modifiers
    const totalMultiplier = activeModifiers.reduce((total, modifier) => {
      return total * modifier.multiplier;
    }, 1.0);

    return speedData.baseSpeed * totalMultiplier;
  }

  /**
   * Add a new speed modifier to a character
   */
  static applySpeedModifier(
    speedData: CharacterSpeedData,
    modifier: Omit<SpeedModifier, 'id' | 'startTime'>
  ): CharacterSpeedData {
    const newModifier: SpeedModifier = {
      ...modifier,
      id: `speed_mod_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      startTime: Date.now(),
    };

    const updatedModifiers = [...speedData.modifiers, newModifier];
    const currentEffectiveSpeed = this.getCurrentSpeed({
      ...speedData,
      modifiers: updatedModifiers,
    });

    return {
      ...speedData,
      modifiers: updatedModifiers,
      currentEffectiveSpeed,
    };
  }

  /**
   * Remove a specific speed modifier by ID
   */
  static removeSpeedModifier(speedData: CharacterSpeedData, modifierId: string): CharacterSpeedData {
    const updatedModifiers = speedData.modifiers.filter(modifier => modifier.id !== modifierId);
    const currentEffectiveSpeed = this.getCurrentSpeed({
      ...speedData,
      modifiers: updatedModifiers,
    });

    return {
      ...speedData,
      modifiers: updatedModifiers,
      currentEffectiveSpeed,
    };
  }

  /**
   * Clean up expired speed modifiers
   */
  static cleanupExpiredModifiers(speedData: CharacterSpeedData): CharacterSpeedData {
    const currentTime = Date.now();
    const activeModifiers = speedData.modifiers.filter(modifier => {
      if (modifier.duration === 0) return true; // Keep permanent modifiers
      return (currentTime - modifier.startTime) < modifier.duration;
    });

    if (activeModifiers.length === speedData.modifiers.length) {
      return speedData; // No changes needed
    }

    const currentEffectiveSpeed = this.getCurrentSpeed({
      ...speedData,
      modifiers: activeModifiers,
    });

    return {
      ...speedData,
      modifiers: activeModifiers,
      currentEffectiveSpeed,
    };
  }

  /**
   * Create initial speed data for a character
   */
  static createCharacterSpeedData(speedModeId: number): CharacterSpeedData {
    const baseSpeed = this.getBaseSpeed(speedModeId);
    return {
      speedModeId,
      baseSpeed,
      modifiers: [],
      currentEffectiveSpeed: baseSpeed,
    };
  }

  /**
   * Get all speed mode base speeds for reference/debugging
   */
  static getAllBaseSpeeds(): Record<number, number> {
    return { ...this.speedModeBaseSpeeds };
  }

  /**
   * Update base speed for a specific speed mode (for development/testing)
   */
  static setBaseSpeed(speedModeId: number, speed: number): void {
    this.speedModeBaseSpeeds[speedModeId] = speed;
  }
}
